// Copyright (c) Microsoft Corporation.
// Licensed under the MIT license.

import React, { useState, useEffect } from 'react';
import InterviewCard from '../../../components/Interview/InterviewCard';
import ProgressIndicator from '../../../components/Interview/ProgressIndicator';
import Button from '../../../components/Interview/shared/Button';
import ContactSupportModal from '../../../components/Interview/ContactSupportModal';
import { DeviceCheckStatus } from '../../../types/interview';
import './ReadinessCheck.css';

interface ReadinessCheckProps {
  onStart: () => void | Promise<void>;
}

const ReadinessCheck: React.FC<ReadinessCheckProps> = ({ onStart }) => {
  const [deviceStatus, setDeviceStatus] = useState<DeviceCheckStatus>({
    microphone: 'checking',
    speaker: 'checking',
    internet: 'checking',
    camera: 'checking'
  });
  const [language, setLanguage] = useState('English');
  const [consentGiven, setConsentGiven] = useState(false);
  const [allChecksComplete, setAllChecksComplete] = useState(false);
  const [isSupportModalOpen, setIsSupportModalOpen] = useState(false);
  const [isRetrying, setIsRetrying] = useState(false);

  useEffect(() => {
    performDeviceChecks();
  }, []);

  useEffect(() => {
    const allSuccess = Object.values(deviceStatus).every((status) => status === 'success');
    setAllChecksComplete(allSuccess);
  }, [deviceStatus]);

  const checkMicrophone = async (): Promise<'success' | 'error'> => {
    try {
      const stream = await navigator.mediaDevices.getUserMedia({ audio: true });

      // Check if audio tracks are available
      const audioTracks = stream.getAudioTracks();
      if (audioTracks.length === 0) {
        stream.getTracks().forEach((track) => track.stop());
        return 'error';
      }

      // Test audio levels
      const audioContext = new AudioContext();
      const analyser = audioContext.createAnalyser();
      const microphone = audioContext.createMediaStreamSource(stream);
      microphone.connect(analyser);
      analyser.fftSize = 256;

      const dataArray = new Uint8Array(analyser.frequencyBinCount);

      // Sample audio for 500ms to detect sound
      await new Promise((resolve) => setTimeout(resolve, 500));
      analyser.getByteFrequencyData(dataArray);

      // Clean up
      stream.getTracks().forEach((track) => track.stop());
      audioContext.close();

      return 'success';
    } catch (error) {
      console.error('Microphone check failed:', error);
      return 'error';
    }
  };

  const checkSpeaker = async (): Promise<'success' | 'error'> => {
    try {
      // Create a short beep using Web Audio API
      const audioContext = new AudioContext();
      const oscillator = audioContext.createOscillator();
      const gainNode = audioContext.createGain();

      oscillator.connect(gainNode);
      gainNode.connect(audioContext.destination);

      oscillator.frequency.value = 440; // A4 note
      gainNode.gain.value = 0.1; // Low volume

      oscillator.start(audioContext.currentTime);
      oscillator.stop(audioContext.currentTime + 0.1);

      await new Promise((resolve) => setTimeout(resolve, 200));
      audioContext.close();

      return 'success';
    } catch (error) {
      console.error('Speaker check failed:', error);
      return 'error';
    }
  };

  const checkInternet = async (): Promise<'success' | 'error'> => {
    try {
      // Primary check: Browser online status
      if (!navigator.onLine) {
        return 'error';
      }

      // Secondary check: Network Information API (if available)
      if ('connection' in navigator) {
        const conn = (navigator as any).connection;
        // Check if connection exists and is reasonable quality
        if (conn && conn.effectiveType) {
          // Accept any connection type except offline
          if (conn.effectiveType !== 'offline') {
            return 'success';
          }
        }
      }

      // Tertiary check: Verify with a lightweight network request
      try {
        const controller = new AbortController();
        const timeoutId = setTimeout(() => controller.abort(), 5000);

        // Fetch index.html from your own origin to avoid CORS
        await fetch('/?t=' + Date.now(), {
          method: 'HEAD',
          cache: 'no-cache',
          signal: controller.signal
        });

        clearTimeout(timeoutId);

        // Any response (including 404) means we're connected
        return 'success';
      } catch (fetchError: any) {
        // If fetch fails but browser says we're online, trust the browser
        // (Could be firewall, CORS, or other network policy)
        if (fetchError.name === 'AbortError') {
          console.warn('Internet check timed out, but browser reports online');
        }

        // Trust navigator.onLine as final authority
        return navigator.onLine ? 'success' : 'error';
      }
    } catch (error) {
      console.error('Internet check failed:', error);
      // Final fallback: trust browser's online status
      return navigator.onLine ? 'success' : 'error';
    }
  };

  const checkCamera = async (): Promise<'success' | 'error'> => {
    try {
      const stream = await navigator.mediaDevices.getUserMedia({ video: true });

      // Check if video tracks are available
      const videoTracks = stream.getVideoTracks();
      if (videoTracks.length === 0) {
        stream.getTracks().forEach((track) => track.stop());
        return 'error';
      }

      // Clean up
      stream.getTracks().forEach((track) => track.stop());

      return 'success';
    } catch (error) {
      console.error('Camera check failed:', error);
      return 'error';
    }
  };

  const performDeviceChecks = async (): Promise<void> => {
    const checks: { key: keyof DeviceCheckStatus; fn: () => Promise<'success' | 'error'> }[] = [
      { key: 'microphone', fn: checkMicrophone },
      { key: 'speaker', fn: checkSpeaker },
      { key: 'internet', fn: checkInternet },
      { key: 'camera', fn: checkCamera }
    ];

    for (const check of checks) {
      try {
        const result = await check.fn();
        setDeviceStatus((prev) => ({
          ...prev,
          [check.key]: result
        }));
      } catch (error) {
        console.error(`${check.key} check error:`, error);
        setDeviceStatus((prev) => ({
          ...prev,
          [check.key]: 'error'
        }));
      }
    }
  };

  const getCheckIcon = (type: keyof DeviceCheckStatus): React.ReactNode => {
    const icons = {
      microphone: (
        <svg
          width="19"
          height="19"
          viewBox="0 0 24 24"
          fill="none"
          stroke="currentColor"
          strokeWidth="2"
          strokeLinecap="round"
          strokeLinejoin="round"
        >
          <path d="M12 1a3 3 0 0 0-3 3v8a3 3 0 0 0 6 0V4a3 3 0 0 0-3-3z"></path>
          <path d="M19 10v2a7 7 0 0 1-14 0v-2"></path>
          <line x1="12" y1="19" x2="12" y2="23"></line>
          <line x1="8" y1="23" x2="16" y2="23"></line>
        </svg>
      ),
      speaker: (
        <svg
          width="19"
          height="19"
          viewBox="0 0 24 24"
          fill="none"
          stroke="currentColor"
          strokeWidth="2"
          strokeLinecap="round"
          strokeLinejoin="round"
        >
          <polygon points="11 5 6 9 2 9 2 15 6 15 11 19 11 5"></polygon>
          <path d="M19.07 4.93a10 10 0 0 1 0 14.14M15.54 8.46a5 5 0 0 1 0 7.07"></path>
        </svg>
      ),
      internet: (
        <svg
          width="19"
          height="19"
          viewBox="0 0 24 24"
          fill="none"
          stroke="currentColor"
          strokeWidth="2"
          strokeLinecap="round"
          strokeLinejoin="round"
        >
          <path d="M5 12.55a11 11 0 0 1 14.08 0"></path>
          <path d="M1.42 9a16 16 0 0 1 21.16 0"></path>
          <path d="M8.53 16.11a6 6 0 0 1 6.95 0"></path>
          <line x1="12" y1="20" x2="12.01" y2="20"></line>
        </svg>
      ),
      camera: (
        <svg
          width="19"
          height="19"
          viewBox="0 0 24 24"
          fill="none"
          stroke="currentColor"
          strokeWidth="2"
          strokeLinecap="round"
          strokeLinejoin="round"
        >
          <path d="M23 19a2 2 0 0 1-2 2H3a2 2 0 0 1-2-2V8a2 2 0 0 1 2-2h4l2-3h6l2 3h4a2 2 0 0 1 2 2z"></path>
          <circle cx="12" cy="13" r="4"></circle>
        </svg>
      )
    };
    return icons[type];
  };

  const getCheckTitle = (type: keyof DeviceCheckStatus): string => {
    const titles = {
      microphone: 'Microphone',
      speaker: 'Speaker / headphones',
      internet: 'Internet connection',
      camera: 'Camera (video recording)'
    };
    return titles[type];
  };

  const getCheckDescription = (type: keyof DeviceCheckStatus, status: 'checking' | 'success' | 'error'): string => {
    if (status === 'checking') return 'Checking...';

    if (status === 'error') {
      const errorDescriptions = {
        microphone: 'Not detected. Please check your microphone permissions and try again.',
        speaker: 'Could not play test tone. Please check your audio output.',
        internet: 'Connection issues detected. Please check your internet connection.',
        camera: 'Not detected. Please check your camera permissions.'
      };
      return errorDescriptions[type];
    }

    const successDescriptions = {
      microphone: 'Detected - levels look good. Speak naturally when prompted.',
      speaker: 'Test tone played successfully.',
      internet: 'Stable connection. You are all set.',
      camera: 'Detected - video will be recorded during the interview.'
    };

    return successDescriptions[type];
  };

  const getStatusIcon = (status: 'checking' | 'success' | 'error'): JSX.Element => {
    if (status === 'checking') return <div className="readiness-spinner" />;

    if (status === 'error') {
      return (
        <svg
          width="19"
          height="19"
          viewBox="0 0 24 24"
          fill="none"
          stroke="currentColor"
          strokeWidth="2"
          strokeLinecap="round"
          strokeLinejoin="round"
        >
          <circle cx="12" cy="12" r="10"></circle>
          <line x1="15" y1="9" x2="9" y2="15"></line>
          <line x1="9" y1="9" x2="15" y2="15"></line>
        </svg>
      );
    }

    return (
      <svg
        width="19"
        height="19"
        viewBox="0 0 24 24"
        fill="none"
        stroke="currentColor"
        strokeWidth="2"
        strokeLinecap="round"
        strokeLinejoin="round"
      >
        <path d="M22 11.08V12a10 10 0 1 1-5.93-9.14"></path>
        <polyline points="22 4 12 14.01 9 11.01"></polyline>
      </svg>
    );
  };

  const getCheckItemClass = (_type: keyof DeviceCheckStatus, status: 'checking' | 'success' | 'error'): string => {
    const baseClass = 'readiness-checkItem';
    if (status === 'checking') return `${baseClass} readiness-loading`;
    if (status === 'success') return `${baseClass} readiness-ok`;
    if (status === 'error') return `${baseClass} readiness-fail`;
    return baseClass;
  };

  const canStart = allChecksComplete && consentGiven;
  const hasErrors = Object.values(deviceStatus).some((status) => status === 'error');

  const handleRetry = async (): Promise<void> => {
    setIsRetrying(true);
    // Reset all statuses to checking
    setDeviceStatus({
      microphone: 'checking',
      speaker: 'checking',
      internet: 'checking',
      camera: 'checking'
    });
    await performDeviceChecks();
    setIsRetrying(false);
  };

  const handleSupportClick = (e: React.MouseEvent<HTMLAnchorElement>): void => {
    e.preventDefault();
    setIsSupportModalOpen(true);
  };

  return (
    <div className="identity-container">
      <InterviewCard>
        <ProgressIndicator totalSteps={3} currentStep={3} />

        <h2 className="readiness-heading">Quick readiness check</h2>
        <p className="readiness-subheading">
          Let's make sure everything works before we start. This takes about 30 seconds.
        </p>

        <div className="readiness-checksContainer">
          {(Object.keys(deviceStatus) as (keyof DeviceCheckStatus)[]).map((check) => (
            <div key={check} className={getCheckItemClass(check, deviceStatus[check])}>
              <div className="readiness-checkIcon">{getCheckIcon(check)}</div>
              <div className="readiness-checkContent">
                <p className="readiness-checkTitle">{getCheckTitle(check)}</p>
                <p className="readiness-checkDescription">{getCheckDescription(check, deviceStatus[check])}</p>
              </div>
              <div className="readiness-checkStatus">{getStatusIcon(deviceStatus[check])}</div>
            </div>
          ))}
        </div>

        <div className="readiness-languageSection">
          <label className="readiness-languageLabel">Interview language</label>
          <select className="readiness-languageSelect" value={language} onChange={(e) => setLanguage(e.target.value)}>
            <option value="English">English</option>
          </select>
        </div>

        <div className="readiness-consentSection">
          <div className="readiness-consentCheckbox">
            <input
              type="checkbox"
              className="readiness-checkbox"
              checked={consentGiven}
              onChange={(e) => setConsentGiven(e.target.checked)}
            />
            <div className="readiness-consentText">
              I consent to this interview being <strong>recorded (audio & video)</strong> and to the recording being{' '}
              <strong>stored and used for evaluation by the hiring team</strong>. I understand:
              <ul className="readiness-consentList">
                <li>Video is recorded for evaluation purposes only</li>
                <li>
                  <strong>No facial recognition or biometric data is collected</strong>
                </li>
                <li>Video stored securely for 90 days, then auto-deleted</li>
                <li>A human recruiter reviews the interview and makes final decisions</li>
                <li>I can request deletion via support@interviewhost.com</li>
              </ul>
            </div>
          </div>
        </div>

        {hasErrors && (
          <div style={{ marginBottom: '12px' }}>
            <Button onClick={handleRetry} disabled={isRetrying} variant="secondary">
              {isRetrying ? 'Retrying checks...' : 'Retry failed checks'}
            </Button>
          </div>
        )}

        <Button onClick={onStart} disabled={!canStart}>
          Start interview
        </Button>

        <p className="readiness-footer">
          Having trouble?{' '}
          <a href="#" className="readiness-supportLink" onClick={handleSupportClick}>
            Contact support
          </a>
        </p>
      </InterviewCard>

      <ContactSupportModal isOpen={isSupportModalOpen} onClose={() => setIsSupportModalOpen(false)} />
    </div>
  );
};

export default ReadinessCheck;
